import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "../../lib/settings";
import { configKey, EMPTY_TRACKING } from "../../lib/consent/config";
import { ALL, CONSENT_KEY, DENIED, cleanVendorStorage, persistConsent, readRecord } from "../../lib/consent/storage";
import { configureTracking, revokeTracking } from "../../lib/consent/runtime";
import { ConsentBanner } from "./ConsentBanner";
import { ConsentPreferences } from "./ConsentPreferences";
import "./consent.css";

const Context = createContext(null);
export const useConsent = () => useContext(Context);
export const ConsentProvider = ({ children }) => {
    const settings = useSettings();
    const tracking = settings.tracking || EMPTY_TRACKING;
    const key = configKey(tracking);
    const { pathname } = useLocation();
    const [record, setRecord] = useState(readRecord);
    const [preferences, setPreferences] = useState(false);
    const [notice, setNotice] = useState("");
    const valid = record?.configKey === key && record.expiresAt > Date.now();
    const categories = valid ? record.categories : DENIED;
    const ready = !!settings.tracking;
    const banner = ready && !valid && !pathname.startsWith("/scrivi");
    const openPreferences = useCallback(() => setPreferences(true), []);

    useEffect(() => {
        cleanVendorStorage({ analytics: !categories.analytics, marketing: !categories.marketing });
        if (configureTracking(tracking, ready ? categories : DENIED, pathname)) window.location.reload();
    }, [tracking, categories, ready, pathname]);
    useEffect(() => {
        const sync = (event) => { if (event.key === CONSENT_KEY) setRecord(readRecord()); };
        window.addEventListener("storage", sync);
        const expiry = setInterval(() => { if (record && record.expiresAt <= Date.now()) setRecord(null); }, 60000);
        return () => { window.removeEventListener("storage", sync); clearInterval(expiry); };
    }, [record]);
    useEffect(() => {
        document.body.classList.toggle("cookie-ui-open", banner || preferences);
        return () => document.body.classList.remove("cookie-ui-open");
    }, [banner, preferences]);
    useEffect(() => {
        if (!notice) return;
        const timer = setTimeout(() => setNotice(""), 3500);
        return () => clearTimeout(timer);
    }, [notice]);

    const save = (choices) => {
        const reload = revokeTracking(choices);
        const next = persistConsent(choices, key);
        setRecord(next); setPreferences(false); setNotice("Preferenze cookie salvate.");
        if (reload) window.location.reload();
    };
    const context = useMemo(() => ({ openPreferences, categories }), [openPreferences, categories]);
    return (
        <Context.Provider value={context}>
            {children}
            {banner && !preferences && <ConsentBanner accept={() => save(ALL)} reject={() => save(DENIED)} customize={openPreferences} />}
            {preferences && <ConsentPreferences categories={categories} save={save} close={() => setPreferences(false)} />}
            {notice && <p className="cookie-notice" role="status" data-testid="cookie-saved-notice">{notice}</p>}
        </Context.Provider>
    );
};

export const CookieSettingsButton = ({ testId = "cookie-settings-open" }) => {
    const { openPreferences } = useConsent();
    return <button type="button" className="tlink cookie-settings-link" onClick={openPreferences} data-testid={testId}>Preferenze cookie</button>;
};